import Phaser from 'phaser';
import { GAME } from '../constants';
import { RunData, addAbility, addCurse, addRelic, removeCurse } from '../run/RunState';
import { getRng } from '../utils/Random';
import { generateAbilityReward, generateModifier } from '../cards/ModifierGen';
import { generateRelicReward } from '../relics/Relic';
import { getCurseById, getCursePool } from '../cards/CurseRegistry';
import { CardModifier } from '../types';
import { PIXEL_THEME, FONT_FAMILY, createPixelPanel, drawPixelBackdrop } from '../ui/PixelTheme';
import { fadeIn, fadeOut } from '../ui/SceneTransitions';

type EventTone = 'good' | 'mixed' | 'bad';

interface GameEvent {
  id: string;
  tone: EventTone;
  pack: number;
  title: string;
  flavor: string;
  choices: EventChoice[];
}

interface EventChoice {
  label: string;
  description: string;
  apply: (run: RunData) => string;
}

function applyModifierToRandomCard(run: RunData, modifier: CardModifier): string {
  const rng = getRng();
  if (run.deckNumbers.length === 0) return 'No cards in deck to augment.';
  const target = rng.pick(run.deckNumbers);
  const existing = run.modifiers.get(target);
  if (!existing) {
    run.modifiers.set(target, { ...modifier, effect: { ...modifier.effect } });
    return `Applied ${modifier.name ?? 'augment'} to card ${target}.`;
  }

  if (existing.trigger === modifier.trigger && existing.effect.type === modifier.effect.type) {
    existing.effect.value += modifier.effect.value;
    run.modifiers.set(target, existing);
    return `Stacked ${modifier.name ?? 'augment'} on ${target} (now +${existing.effect.value}).`;
  }

  run.modifiers.set(target, { ...modifier, effect: { ...modifier.effect } });
  return `Replaced existing modifier on card ${target}.`;
}

function addRandomCurse(run: RunData): string {
  const rng = getRng();
  const pool = getCursePool(run.unlockSnapshot);
  if (pool.length === 0) return 'No curse available in current unlock pack.';
  const curse = rng.pick(pool);
  addCurse(run, curse.id);
  return `Gained curse: ${curse.name}.`;
}

function removeRandomCurse(run: RunData): string {
  if (run.curses.length === 0) return 'No curse to remove.';
  const rng = getRng();
  const index = rng.int(0, run.curses.length - 1);
  const removed = removeCurse(run, index);
  const curse = removed ? getCurseById(removed) : null;
  return removed
    ? `Removed curse: ${curse?.name ?? removed}.`
    : 'No curse removed.';
}

function getEvents(run: RunData): GameEvent[] {
  return [
    {
      id: 'blessing_pool',
      tone: 'good',
      pack: 1,
      title: 'BLESSING POOL',
      flavor: 'A still pool mirrors your deck back at you in perfect order.',
      choices: [
        {
          label: 'Drink',
          description: 'Immediate: heal 10 HP. Downside: none. Permanence: this run.',
          apply: (r) => {
            const healed = Math.min(10, r.playerMaxHp - r.playerHp);
            r.playerHp += healed;
            return `Immediate: healed ${healed} HP. Downside: none. Permanence: this run.`;
          },
        },
        {
          label: 'Refine Reflection',
          description: 'Immediate: remove 1 random curse. Downside: none. Permanence: run deck change.',
          apply: (r) => `Immediate: ${removeRandomCurse(r)} Downside: none. Permanence: run deck change.`,
        },
      ],
    },
    {
      id: 'careful_cartographer',
      tone: 'good',
      pack: 1,
      title: 'CAREFUL CARTOGRAPHER',
      flavor: 'A traveler offers notes about upcoming hazards.',
      choices: [
        {
          label: 'Study Route',
          description: 'Immediate: draw +1 for next 2 battles. Downside: none. Permanence: temporary.',
          apply: (r) => {
            r.drawBonusBattles = Math.max(r.drawBonusBattles, 2);
            return 'Immediate: draw +1 set for next 2 battles. Downside: none. Permanence: temporary.';
          },
        },
        {
          label: 'Take Supply Kit',
          description: 'Immediate: if HP <= 50%, heal 14 else heal 6. Downside: none. Permanence: this run.',
          apply: (r) => {
            const amount = r.playerHp <= Math.floor(r.playerMaxHp / 2) ? 14 : 6;
            const healed = Math.min(amount, r.playerMaxHp - r.playerHp);
            r.playerHp += healed;
            return `Immediate: healed ${healed} HP. Downside: none. Permanence: this run.`;
          },
        },
      ],
    },
    {
      id: 'vault_of_order',
      tone: 'good',
      pack: 2,
      title: 'VAULT OF ORDER',
      flavor: 'Three runes align and unlock a hidden compartment.',
      choices: [
        {
          label: 'Claim Relic',
          description: 'Immediate: gain common relic. Downside: none. Permanence: persistent this run.',
          apply: (r) => {
            const relic = generateRelicReward('common', r.relics.map(rel => rel.id));
            if (!relic) return 'Immediate: vault empty. Downside: none. Permanence: none.';
            addRelic(r, relic);
            return `Immediate: gained relic ${relic.name}. Downside: none. Permanence: persistent this run.`;
          },
        },
        {
          label: 'Archive Knowledge',
          description: 'Immediate: learn ability (if available). Downside: none. Permanence: persistent this run.',
          apply: (r) => {
            const ability = generateAbilityReward({
              battleNumber: r.battlesWon + 1,
              nodeType: 'event',
              unlockSnapshot: r.unlockSnapshot,
              ownedAbilityIds: r.abilities.map(a => a.id),
            });
            if (!ability) return 'Immediate: no ability available. Downside: none. Permanence: none.';
            addAbility(r, ability);
            return `Immediate: learned ${ability.name}. Downside: none. Permanence: persistent this run.`;
          },
        },
      ],
    },

    // Mixed tone events (risk/reward with optional curse)
    {
      id: 'forbidden_ledger',
      tone: 'mixed',
      pack: 1,
      title: 'FORBIDDEN LEDGER',
      flavor: 'A book bound in wire offers power in exchange for burden.',
      choices: [
        {
          label: 'Sign It',
          description: 'Immediate: gain strong augment. Downside: gain 1 curse. Permanence: both persist for run.',
          apply: (r) => {
            const modifier = generateModifier({
              battleNumber: r.battlesWon + 2,
              nodeType: 'event',
              unlockSnapshot: r.unlockSnapshot,
              minRarity: 'uncommon',
            }).modifier;
            const aug = applyModifierToRandomCard(r, modifier);
            const curse = addRandomCurse(r);
            return `Immediate: ${aug} Downside: ${curse} Permanence: both persist for run.`;
          },
        },
        {
          label: 'Close It',
          description: 'Immediate: heal 5 HP. Downside: no bonus power. Permanence: none.',
          apply: (r) => {
            const healed = Math.min(5, r.playerMaxHp - r.playerHp);
            r.playerHp += healed;
            return `Immediate: healed ${healed} HP. Downside: no bonus power. Permanence: none.`;
          },
        },
      ],
    },
    {
      id: 'blood_engine',
      tone: 'mixed',
      pack: 1,
      title: 'BLOOD ENGINE',
      flavor: 'A machine beats like a second heart when you approach.',
      choices: [
        {
          label: 'Prime Engine',
          description: 'Immediate: gain uncommon relic. Downside: lose 8 HP and gain 1 curse. Permanence: run-long.',
          apply: (r) => {
            r.playerHp = Math.max(1, r.playerHp - 8);
            const relic = generateRelicReward('uncommon', r.relics.map(rel => rel.id));
            if (relic) addRelic(r, relic);
            const curse = addRandomCurse(r);
            return `Immediate: ${relic ? `gained ${relic.name}` : 'no relic available'}. Downside: lost 8 HP, ${curse} Permanence: run-long.`;
          },
        },
        {
          label: 'Bleed Off Pressure',
          description: 'Immediate: remove 1 curse if present. Downside: lose 4 HP. Permanence: run deck change.',
          apply: (r) => {
            r.playerHp = Math.max(1, r.playerHp - 4);
            const removed = removeRandomCurse(r);
            return `Immediate: ${removed} Downside: lost 4 HP. Permanence: run deck change.`;
          },
        },
      ],
    },
    {
      id: 'hollow_oracle',
      tone: 'mixed',
      pack: 2,
      title: 'HOLLOW ORACLE',
      flavor: 'It predicts your next draws, but asks for a toll.',
      choices: [
        {
          label: 'Pay the Toll',
          description: 'Immediate: draw +1 next 3 battles. Downside: gain Heavy Hand curse. Permanence: mixed.',
          apply: (r) => {
            r.drawBonusBattles = Math.max(r.drawBonusBattles, 3);
            addCurse(r, 'heavy_hand');
            return 'Immediate: draw +1 for next 3 battles. Downside: gained Heavy Hand curse. Permanence: mixed.';
          },
        },
        {
          label: 'Reject Prophecy',
          description: 'Immediate: gain +4 max HP. Downside: none. Permanence: this run.',
          apply: (r) => {
            r.playerMaxHp += 4;
            r.playerHp += 4;
            return `Immediate: max HP increased to ${r.playerMaxHp}. Downside: none. Permanence: this run.`;
          },
        },
      ],
    },
    {
      id: 'split_workshop',
      tone: 'mixed',
      pack: 3,
      title: 'SPLIT WORKSHOP',
      flavor: 'One side sharpens cards, the other drains their color.',
      choices: [
        {
          label: 'Sharpen',
          description: 'Immediate: add card +9 or -9. Downside: gain 1 curse. Permanence: run deck change.',
          apply: (r) => {
            const rng = getRng();
            const n = rng.pick([9, -9]);
            if (!r.deckNumbers.includes(n)) r.deckNumbers.push(n);
            const curse = addRandomCurse(r);
            return `Immediate: added card ${n}. Downside: ${curse} Permanence: run deck change.`;
          },
        },
        {
          label: 'Drain',
          description: 'Immediate: transform one random curse into a negative number card. Downside: lose 3 HP. Permanence: run deck change.',
          apply: (r) => {
            r.playerHp = Math.max(1, r.playerHp - 3);
            if (r.curses.length === 0) {
              return 'Immediate: no curse to transform. Downside: lost 3 HP. Permanence: none.';
            }
            const rng = getRng();
            const index = rng.int(0, r.curses.length - 1);
            const removed = removeCurse(r, index);
            const transformed = rng.int(-6, -2);
            if (!r.deckNumbers.includes(transformed)) r.deckNumbers.push(transformed);
            const curseName = removed ? getCurseById(removed)?.name ?? removed : 'Unknown curse';
            return `Immediate: transformed ${curseName} into card ${transformed}. Downside: lost 3 HP. Permanence: run deck change.`;
          },
        },
      ],
    },

    // Bad tone events with mitigation choices
    {
      id: 'hex_pylon',
      tone: 'bad',
      pack: 1,
      title: 'HEX PYLON',
      flavor: 'A pulsing obelisk marks your deck with static.',
      choices: [
        {
          label: 'Take the Hit',
          description: 'Immediate: gain 1 random curse. Downside: curse persists. Permanence: run-long.',
          apply: (r) => `Immediate: ${addRandomCurse(r)} Downside: curse persists. Permanence: run-long.`,
        },
        {
          label: 'Break It',
          description: 'Immediate: gain 1 random curse. Downside: lose 10 HP. Permanence: run-long.',
          apply: (r) => {
            r.playerHp = Math.max(1, r.playerHp - 10);
            const curse = addRandomCurse(r);
            return `Immediate: ${curse} Downside: lost 10 HP. Permanence: run-long.`;
          },
        },
      ],
    },
    {
      id: 'tax_auditor',
      tone: 'bad',
      pack: 2,
      title: 'TAX AUDITOR',
      flavor: 'A spectral clerk demands payment from your strongest line.',
      choices: [
        {
          label: 'Pay in Blood',
          description: 'Immediate: keep deck unchanged. Downside: lose 9 HP. Permanence: none.',
          apply: (r) => {
            r.playerHp = Math.max(1, r.playerHp - 9);
            return 'Immediate: deck unchanged. Downside: lost 9 HP. Permanence: none.';
          },
        },
        {
          label: 'Pay in Cards',
          description: 'Immediate: remove one highest |number| card. Downside: lose power. Permanence: run deck change.',
          apply: (r) => {
            const sorted = [...r.deckNumbers].sort((a, b) => Math.abs(b) - Math.abs(a));
            const removed = sorted[0];
            r.deckNumbers = r.deckNumbers.filter(n => n !== removed);
            r.modifiers.delete(removed);
            return `Immediate: removed card ${removed}. Downside: deck weakened. Permanence: run deck change.`;
          },
        },
      ],
    },
    {
      id: 'cruel_exchange',
      tone: 'bad',
      pack: 3,
      title: 'CRUEL EXCHANGE',
      flavor: 'A portal opens. You can bargain, but not win outright.',
      choices: [
        {
          label: 'Accept Curse Bundle',
          description: 'Immediate: gain uncommon relic. Downside: gain 2 curses. Permanence: run-long.',
          apply: (r) => {
            const relic = generateRelicReward('uncommon', r.relics.map(rel => rel.id));
            if (relic) addRelic(r, relic);
            const c1 = addRandomCurse(r);
            const c2 = addRandomCurse(r);
            return `Immediate: ${relic ? `gained ${relic.name}` : 'no relic available'}. Downside: ${c1} ${c2} Permanence: run-long.`;
          },
        },
        {
          label: 'Force Exit',
          description: 'Immediate: lose 12 HP, remove 1 random curse if any. Downside: heavy HP loss. Permanence: mixed.',
          apply: (r) => {
            r.playerHp = Math.max(1, r.playerHp - 12);
            const removed = removeRandomCurse(r);
            return `Immediate: ${removed} Downside: lost 12 HP. Permanence: mixed.`;
          },
        },
      ],
    },
  ];
}

function pickWeightedTone(act: number): EventTone {
  const rng = getRng();
  const roll = rng.int(1, 100);

  // Base: good 25 / mixed 45 / bad 30
  // Act 2 shift: good 15 / mixed 50 / bad 35
  const goodCutoff = act >= 2 ? 15 : 25;
  const mixedCutoff = goodCutoff + (act >= 2 ? 50 : 45);

  if (roll <= goodCutoff) return 'good';
  if (roll <= mixedCutoff) return 'mixed';
  return 'bad';
}

function pickEvent(run: RunData): GameEvent {
  const rng = getRng();
  const tone = pickWeightedTone(run.act);
  const availableEvents = getEvents(run).filter(event => event.pack <= run.unlockSnapshot.eventPack);
  const tonePool = availableEvents.filter(event => event.tone === tone);
  const pool = tonePool.length > 0 ? tonePool : availableEvents;
  return rng.pick(pool);
}

export class EventScene extends Phaser.Scene {
  private chosen = false;

  constructor() {
    super({ key: 'EventScene' });
  }

  create(data: { run: RunData }): void {
    this.chosen = false;
    const { run } = data;
    run.abilities = run.abilities ?? [];
    run.curses = run.curses ?? [];
    run.unlockSnapshot = run.unlockSnapshot ?? {
      maxRarity: 'common',
      eventPack: 1,
      cursePack: 1,
      abilityPack: 1,
    };
    fadeIn(this);
    const cx = GAME.WIDTH / 2;

    const event = pickEvent(run);
    const toneColor = event.tone === 'good' ? '#7de39d' : event.tone === 'mixed' ? '#ffd379' : '#ff8c86';

    drawPixelBackdrop(this);

    const headerPanel = createPixelPanel(this, cx, 52, GAME.WIDTH - 104, 86, {
      fill: 0x21172a,
      stroke: 0xbf663f,
      accent: 0xffd58a,
    });
    headerPanel.elements.forEach(el => el.setDepth(1));

    const flavorPanel = createPixelPanel(this, cx, 136, GAME.WIDTH - 170, 76, {
      fill: 0x131b2c,
      stroke: 0x4d739f,
      accent: 0x8eb7e6,
    });
    flavorPanel.elements.forEach(el => el.setDepth(1));

    const statsPanel = createPixelPanel(this, cx, 186, GAME.WIDTH - 240, 38, {
      fill: 0x152238,
      stroke: 0x4f88ad,
      accent: 0xa3def0,
    });
    statsPanel.elements.forEach(el => el.setDepth(1));

    this.add.text(cx, 52, event.title, {
      fontSize: '16px',
      fontFamily: FONT_FAMILY,
      color: '#ffe2ac',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(2);

    this.add.text(cx, 86, `[${event.tone.toUpperCase()} EVENT]`, {
      fontSize: '11px',
      fontFamily: FONT_FAMILY,
      color: toneColor,
    }).setOrigin(0.5).setDepth(2);

    this.add.text(cx, 136, event.flavor, {
      fontSize: '9px',
      fontFamily: FONT_FAMILY,
      color: '#d2def5',
      align: 'center',
      wordWrap: { width: GAME.WIDTH - 240 },
    }).setOrigin(0.5).setDepth(2);

    this.add.text(cx, 186, `HP ${run.playerHp}/${run.playerMaxHp}  |  Deck ${run.deckNumbers.length}  |  Curses ${run.curses.length}  |  Event pack ${run.unlockSnapshot.eventPack}`, {
      fontSize: '11px',
      fontFamily: FONT_FAMILY,
      color: PIXEL_THEME.textMuted,
    }).setOrigin(0.5).setDepth(2);

    const choiceStartY = 292;
    const choiceSpacing = 134;

    for (let i = 0; i < event.choices.length; i++) {
      const choice = event.choices[i];
      const y = choiceStartY + i * choiceSpacing;
      const card = createPixelPanel(this, cx, y, GAME.WIDTH - 180, 114, {
        fill: 0x1e2438,
        stroke: event.tone === 'good' ? 0x4fb47a : event.tone === 'mixed' ? 0xc68f4c : 0xbd5b60,
        accent: event.tone === 'good' ? 0x8ef0b5 : event.tone === 'mixed' ? 0xffd28c : 0xff9da3,
      });
      card.elements.forEach(el => el.setDepth(3));
      const bg = card.bg.setInteractive({ useHandCursor: true });

      this.add.text(cx, y - 24, choice.label, {
        fontSize: '12px',
        fontFamily: FONT_FAMILY,
        color: '#f4f0e6',
        fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(4);

      this.add.text(cx, y + 14, choice.description, {
        fontSize: '11px',
        fontFamily: FONT_FAMILY,
        color: '#cfdaf0',
        align: 'center',
        wordWrap: { width: GAME.WIDTH - 240 },
      }).setOrigin(0.5).setDepth(4);

      bg.on('pointerover', () => bg.setStrokeStyle(3, 0xffe0a4, 0.98));
      bg.on('pointerout', () => bg.setStrokeStyle(2, event.tone === 'good' ? 0x4fb47a : event.tone === 'mixed' ? 0xc68f4c : 0xbd5b60, 1));
      bg.on('pointerdown', () => {
        if (this.chosen) return;
        this.chosen = true;
        const resultText = choice.apply(run);
        this.showResult(resultText, run);
      });
    }

    this.add.text(cx, GAME.HEIGHT - 28, 'Choose one option. Effects are applied immediately.', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#9bb2dc',
    }).setOrigin(0.5).setDepth(2);
  }

  private showResult(text: string, run: RunData): void {
    const cx = GAME.WIDTH / 2;
    const resultPanel = createPixelPanel(this, cx, GAME.HEIGHT - 92, GAME.WIDTH - 150, 96, {
      fill: 0x1f1e2f,
      stroke: 0xd08a52,
      accent: 0xffd58a,
    });
    resultPanel.elements.forEach(el => el.setDepth(10));

    this.add.text(cx, GAME.HEIGHT - 104, 'RESULT', {
      fontSize: '8px',
      fontFamily: FONT_FAMILY,
      color: '#ffd9a3',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    this.add.text(cx, GAME.HEIGHT - 82, text, {
      fontSize: '9px',
      fontFamily: FONT_FAMILY,
      color: '#f2ece1',
      fontStyle: 'bold',
      align: 'center',
      lineSpacing: 3,
      wordWrap: { width: GAME.WIDTH - 220 },
    }).setOrigin(0.5).setDepth(11);

    this.add.text(cx, GAME.HEIGHT - 46, 'Returning to map...', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#9db2db',
    }).setOrigin(0.5).setDepth(11);

    this.time.delayedCall(1700, () => {
      fadeOut(this).then(() => this.scene.start('MapScene', { run }));
    });
  }
}
